import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

/**
 * Qué cambió, en una tabla de «antes» y «después».
 *
 * Antes esto era el JSON crudo de la bitácora, uno al lado del otro. Para quien
 * audita —que no es programador— eso no dice nada: hay que saber leer llaves y
 * comparar dos bloques a ojo para encontrar el único campo que se movió.
 *
 * Aquí se comparan campo por campo y **sólo se muestra lo que cambió**, con el
 * nombre del dato en español y los valores en lenguaje llano.
 */
interface Props {
  anterior: unknown;
  nuevo: unknown;
  /**
   * Identificadores resueltos a nombres. La bitácora guarda identificadores
   * —tiene que hacerlo: un nombre cambia y el registro dejaría de ser fiel—,
   * pero mostrarlos así convertía el cambio más común, pasar de un grupo a otro,
   * en «1 elemento → 1 elemento», que no dice absolutamente nada.
   */
  nombres: Map<string, string>;
}

export function CambiosAuditoria({ anterior, nuevo, nombres }: Props) {
  const { t } = useTranslation();

  const antes = comoObjeto(anterior);
  const despues = comoObjeto(nuevo);
  const campos = [...new Set([...Object.keys(antes), ...Object.keys(despues)])];

  // Sin foto previa no hay comparación posible: se muestra lo que quedó.
  const hayComparacion = Object.keys(antes).length > 0;
  const filas = campos
    .map((campo) => ({ campo, antes: antes[campo], despues: despues[campo] }))
    .filter((fila) =>
      hayComparacion
        ? JSON.stringify(fila.antes) !== JSON.stringify(fila.despues) &&
          fila.despues !== undefined
        : fila.despues !== undefined,
    );

  if (filas.length === 0) {
    return <p className="vacio">{t('audit.noChanges')}</p>;
  }

  return (
    <>
      <div className="tabla-envoltura">
        <table>
          <thead>
            <tr>
              <th>{t('audit.field')}</th>
              {hayComparacion && <th>{t('audit.before')}</th>}
              <th>{hayComparacion ? t('audit.after') : t('audit.field')}</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((fila) => (
              <tr key={fila.campo}>
                <td>{t(`audit.fields.${fila.campo}`, { defaultValue: fila.campo })}</td>
                {hayComparacion && (
                  <td className="cambio__antes">{legible(fila.antes, t, nombres, fila.campo)}</td>
                )}
                <td className="cambio__despues">
                  {legible(fila.despues, t, nombres, fila.campo)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tarjeta__pie">{t('audit.namesNote')}</p>
    </>
  );
}

function comoObjeto(valor: unknown): Record<string, unknown> {
  return valor && typeof valor === 'object' && !Array.isArray(valor)
    ? (valor as Record<string, unknown>)
    : {};
}

const ES_IDENTIFICADOR = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Un valor guardado, dicho en español. */
function legible(
  valor: unknown,
  t: TFunction,
  nombres: Map<string, string>,
  campo?: string,
): string {
  if (valor === undefined || valor === null || valor === '') return t('audit.notSet');
  // El motivo de una baja se guarda como código —«UPLOADED_BY_MISTAKE»—, que
  // es lo estable; quien audita lo lee con palabras.
  if (campo === 'reason' && typeof valor === 'string') {
    return t(`audit.deletionReasons.${valor}`, { defaultValue: valor });
  }
  // Qué delató una posible captura (ADR-0029): se guarda el código.
  if (campo === 'signal' && typeof valor === 'string') {
    return t(`audit.captureSignals.${valor}`, { defaultValue: valor });
  }
  if (typeof valor === 'boolean') return valor ? t('audit.yes') : t('audit.no');

  if (Array.isArray(valor)) {
    if (valor.length === 0) return t('audit.notSet');
    return valor.map((elemento) => legible(elemento, t, nombres)).join(', ');
  }

  if (typeof valor === 'object') {
    // Una asignación de permiso: lo que importa es el rol, no su ámbito interno.
    const objeto = valor as Record<string, unknown>;
    const rol = objeto['roleCode'] ?? objeto['roleId'];
    if (rol) return legible(rol, t, nombres);
    return Object.entries(objeto)
      .map(
        ([clave, dentro]) =>
          `${t(`audit.fields.${clave}`, { defaultValue: clave })}: ${legible(dentro, t, nombres)}`,
      )
      .join(' · ');
  }

  const texto = String(valor);
  if (ES_IDENTIFICADOR.test(texto)) {
    return nombres.get(texto) ?? t('audit.unknownName');
  }
  // Una fecha ISO se muestra como día.
  if (/^\d{4}-\d{2}-\d{2}T/.test(texto)) return texto.slice(0, 10);
  return texto;
}
